import { useState } from "react";
import { Link } from "react-router-dom";
import logoSrc from "@/assets/logo.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus("success");
    setEmail("");
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 text-white">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-lg font-bold text-white mb-1">Stay ahead of the curve</h4>
              <p className="text-sm text-slate-400">Get Skool growth tips, AI marketing strategies, and product updates — free.</p>
            </div>
            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-3 w-full md:w-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-violet-400 w-full sm:w-64"
              />
              <button
                type="submit"
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
              >
                Subscribe Free
              </button>
            </form>
            {status === "success" && (
              <p className="text-sm text-emerald-400 font-medium">You&apos;re in! Check your inbox.</p>
            )}
            {status === "error" && (
              <p className="text-sm text-rose-400">Something went wrong. Try again.</p>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="cursor-pointer">
              <img
                src={logoSrc}
                alt="ClickSendAI"
                className="h-10 w-auto object-contain mb-4"
              />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              AI-powered marketing automation for community owners, creators, and businesses. Grow externally across 30+ networks — completely on autopilot.
            </p>
            <div className="flex items-center gap-3 mt-5">
              {[
                { icon: "ri-facebook-fill", href: "#" },
                { icon: "ri-twitter-x-fill", href: "#" },
                { icon: "ri-instagram-line", href: "#" },
                { icon: "ri-linkedin-fill", href: "#" },
                { icon: "ri-youtube-fill", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-violet-500 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 mt-5">
              {["SOC 2", "GDPR", "SSL"].map((badge) => (
                <span key={badge} className="px-2.5 py-1 rounded-md bg-white/10 text-xs text-slate-400 font-medium border border-white/10">
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Product</h4>
            <ul className="space-y-2.5">
              <li><Link to="/features" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Features</Link></li>
              <li><Link to="/pricing" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Pricing</Link></li>
              <li><Link to="/product/ai-content" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">AI Content</Link></li>
              <li><Link to="/product/forum-agent" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Reddit &amp; Quora AI Agent</Link></li>
              <li><Link to="/features/landing-pages" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Landing Pages</Link></li>
              <li><Link to="/product/scheduling" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Smart Scheduling</Link></li>
              <li><Link to="/product/analytics" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Analytics</Link></li>
              <li><Link to="/affiliate-tools" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Affiliate Tools</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/blog" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Blog</Link></li>
              <li><Link to="/solutions/affiliate-marketers" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Affiliates</Link></li>
              <li><Link to="/contact" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Contact</Link></li>
              <li><Link to="/privacy" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Terms of Service</Link></li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Solutions</h4>
            <ul className="space-y-2.5">
              <li><Link to="/solutions/skool-groups" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Skool Groups</Link></li>
              <li><Link to="/solutions/course-creators" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Course Creators</Link></li>
              <li><Link to="/solutions/coaches" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Coaches</Link></li>
              <li><Link to="/solutions/marketing-agencies" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Agencies</Link></li>
              <li><Link to="/contact" className="text-sm text-slate-400 hover:text-violet-400 transition-colors cursor-pointer">Help Center</Link></li>
            </ul>

            {/* Contact info */}
            <div className="mt-6 space-y-2">
              <a href="mailto:hello@clicksendai.com" className="flex items-center gap-2 text-xs text-slate-500 hover:text-violet-400 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-sm"></i>
                </div>
                hello@clicksendai.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} ClickSendAI. All rights reserved. Not affiliated with Skool.com.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-xs text-slate-500 hover:text-violet-400 transition-colors cursor-pointer">Privacy</Link>
            <Link to="/terms" className="text-xs text-slate-500 hover:text-violet-400 transition-colors cursor-pointer">Terms</Link>
            <Link to="/contact" className="text-xs text-slate-500 hover:text-violet-400 transition-colors cursor-pointer">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
